import axios, { AxiosProgressEvent } from "axios";
import * as client from "../client";
import { TemplateUseCase } from "../pages/specialty-assistant/template/data";
import { ROUTES } from "../routes";
import { buildChannel } from "../util";
import * as Token from "./auth_token";
export * as apiTypes from "../client/types.gen";
import * as config from "../config";

const baseUrl = () => {
  const channel = buildChannel();
  switch (channel) {
    case "prod":
      return config.BASE_URL_PROD;
    case "dev":
      return config.BASE_URL_DEV;
    case "dev-vm":
      return config.BASE_URL_DEV_VM;
  }
};

const fetchBinary = async (options: {
  method: "get" | "post";
  path: string;
  body?: unknown;
  contentType?: string;
}): Promise<{ data: Blob; filename?: string }> => {
  const url = `${baseUrl()}${options.path}`;
  const config = {
    responseType: "arraybuffer" as const,
    headers: { Authorization: `Bearer ${Token.getToken()}` },
  };
  const response =
    options.method === "get"
      ? await axios.get(url, config)
      : await axios.post(url, options.body, config);
  const headers = response.headers;
  const getHeader = (name: string): string | undefined => {
    if (typeof headers.get === "function") {
      const value = headers.get(name);
      if (typeof value === "string") return value;
    }
    return undefined;
  };
  const disposition = getHeader("Content-Disposition");
  const blob = new Blob([response.data as ArrayBuffer], {
    type:
      options.contentType ??
      getHeader("Content-Type") ??
      "application/octet-stream",
  });
  return {
    data: blob,
    filename: disposition
      ? /filename="([^"]*)"/.exec(disposition)?.[1]
      : undefined,
  };
};

client.OpenAPI.BASE = baseUrl();
client.OpenAPI.interceptors.request.use((request) => {
  const token = Token.getToken();
  if (token) {
    if (request.headers === undefined) {
      request.headers = {};
    }
    request.headers["Authorization"] = `Bearer ${token}`;
  }

  return request;
});

client.OpenAPI.interceptors.response.use((response) => {
  if (response.status === 401 && !response.config.url?.endsWith("login")) {
    Token.logOut();
    window.location.href = ROUTES.LANDING;
  }
  return response;
});

let URL_PROGRESS_CALLBACKS: [string, (e: AxiosProgressEvent) => void][] = [];

client.OpenAPI.interceptors.request.use((request) => {
  request.onUploadProgress = (e: AxiosProgressEvent) => {
    for (const [url, cb] of URL_PROGRESS_CALLBACKS) {
      if (request.url?.startsWith(url)) {
        cb(e);
      }
    }
  };
  return request;
});

export const login = async (
  username: string,
  password: string
): Promise<
  | { success: true; clientId: string; user: client.User }
  | { success: false; failureReason: string }
> => {
  return await client
    .loginLoginPost({ formData: { username, password } })
    .then((r) => {
      Token.setToken(r.access_token);
      return {
        success: true as const,
        clientId: r.client_id,
        user: r.user,
      };
    })
    .catch((rawE: client.ApiError) => {
      if (rawE.status === 422) {
        const e = rawE as client.HttpValidationError;
        console.error("login failed: ", e.detail);
      }
      return { success: false, failureReason: "unknown" };
    });
};

export const logAmplitudeEventRemotely = async (
  eventName: string,
  eventProperties: {
    [key: string]: string | number;
  }
) => {
  return client.logEventLogEventPost({
    requestBody: { event_name: eventName, event_properties: eventProperties },
  });
};

export type LOB =
  client.AsgardListReportsPostAsgardListReportsPostData["requestBody"]["lobs"][number];
export const listReports = async (
  lobs: LOB[]
): Promise<client.ReportMetadataResponse[]> => {
  return client
    .asgardListReportsPostAsgardListReportsPost({ requestBody: { lobs } })
    .then((response) => response.reports);
};

export const listReport = async (reportId: string) => {
  return client.asgardListReportAsgardListReportReportIdGet({ reportId });
};

export const fetchEmailThreads = async (reportId: string) => {
  return client.asgardEmailThreadsAsgardEmailThreadsReportIdGet({ reportId });
};

export type UpdateReport = {
  assignee?: client.UpdateAsignee;
  status?: client.UpdateStatus;
  additionalAssignees?: client.AdditionalAssigneeInfo;
  sovs?: client.Sov[];
  lossRunInfo?: client.LossRunInfo;
  reviewQuestionResponse?: client.ReviewQuestionResponse;
  companyInfo?: client.CompanyInfo;
  attachmentInfo?: client.AttachmentInfo;
  referralText?: client.ReferralText;
  archiveAt?: boolean;
  rerunReport?: boolean;
  brokerContact?: client.UpdateBrokerContact;
  limitAndSublimitInfo?: client.LimitAndSublimitInfo;
  policyInfo?: client.PolicyInfo;
  sublimitInfo?: client.SublimitInfo;
  deductibleInfo?: client.AdvancedDeductibleInfo;
  advancedLimitInfo?: client.AdvancedLimitInfo;
  layerInfo?: client.Layer[];
};
export const updateReport = async (
  reportId: string,
  updates: UpdateReport
): Promise<client.UpdateReportResponse> => {
  return await client.asgardUpdateReportAsgardUpdateReportPost({
    requestBody: {
      report_id: reportId,
      update_assignee: updates.assignee ?? null,
      update_status: updates.status ?? null,
      update_additional_assignees: updates.additionalAssignees ?? null,
      // Addresses are never mutated via this API (They are dropped on the backend)
      // However, these represent a lot of data, so don't send them over the wire.
      update_sovs: updates.sovs
        ? updates.sovs.map((sov) => ({ ...sov, addresses: [] }))
        : null,
      update_loss_runs_info: updates.lossRunInfo ?? null,
      update_review_question_response: updates.reviewQuestionResponse ?? null,
      update_company_info: updates.companyInfo ?? null,
      update_attachment_info: updates.attachmentInfo ?? null,
      rerun_report: updates.rerunReport ?? null,
      update_referral_text: updates.referralText ?? null,
      update_broker_contact: updates.brokerContact ?? null,
      update_limit_info: updates.limitAndSublimitInfo ?? null,
      update_policy_info: updates.policyInfo ?? null,
      update_sublimit_info: updates.sublimitInfo ?? null,
      update_deductible_info: updates.deductibleInfo ?? null,
      update_advanced_limit_info: updates.advancedLimitInfo ?? null,
      update_layer_info: updates.layerInfo ?? null,
    },
  });
};

export type UpdateAddresses = {
  addresses: client.Address[];
};
export const updateAddresses = async (
  report_id: string,
  update: UpdateAddresses
): Promise<client.UpdateAddressResponse> => {
  return await client.asgardUpdateAddressAsgardUpdateAddressPost({
    requestBody: { report_id, addresses: update.addresses },
  });
};

export const addManualAddress = async (
  report_id: string,
  address: client.RawAddress
): Promise<client.AddManualAddressResponse> => {
  return await client.asgardAddManualAddressAsgardAddManualAddressPost({
    requestBody: { report_id, address: address },
  });
};

export const downloadLossRun = async (
  parsedLossRuns: client.UserGeneratedLossRunByYear[]
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_loss_run",
    body: { loss_run_years: parsedLossRuns },
  });
  return data;
};

export const downloadPdfZip = async (folderName: string) => {
  const { data } = await fetchBinary({
    method: "get",
    path: `/adonis/download_pdfs?folder_name=${folderName}`,
  });
  return data;
};

export const listUsers = async () => {
  return await client.listUsersListUsersPost();
};

export const listCRMContacts = async () => {
  return await client.listContactsEndpointCrmContactsGet();
};

export const createCRMContact = async (
  requestBody: client.CreateContactRequest
): Promise<client.CreateContactResponse> => {
  return await client.createContactEndpointCrmContactPost({ requestBody });
};

export const updateCRMContact = async (
  contactId: string,
  requestBody: client.UpdateContactRequest
): Promise<client.UpdateContactResponse> => {
  return await client.updateContactEndpointCrmContactContactIdPost({
    contactId,
    requestBody,
  });
};

export const searchAddresses = async (
  lobs: LOB[],
  query: string,
  terms: client.TermSearch[]
) => {
  return await client.asgardSearchReportAddressesAsgardSearchReportAddressesPost(
    { requestBody: { raw_query: query, term_query: terms, lobs } }
  );
};

export const listAdditionalFiles = async (
  reportId: string
): Promise<string[]> => {
  return (
    await client.asgardListAdditionalFilesAsgardListAdditionalFilesReportIdPost(
      { reportId }
    )
  ).files;
};

export const uploadAdditionalFile = async (
  reportId: string,
  files: File[],
  onUploadProgress: (e: AxiosProgressEvent) => void,
  isScreenshot: boolean
): Promise<string[]> => {
  const callback = (e: AxiosProgressEvent) => onUploadProgress(e);
  URL_PROGRESS_CALLBACKS.push([
    `${client.OpenAPI.BASE}/asgard/upload_additional_file`,
    callback,
  ]);
  try {
    return (
      await client.asgardUploadAdditionalFileAsgardUploadAdditionalFilesPost({
        reportId: reportId,
        classificationForAllFiles: isScreenshot ? "screenshot" : null,
        sourceForAllFiles: isScreenshot ? "screenshot_tool" : "user_upload",
        formData: {
          files,
        },
      })
    ).files;
  } finally {
    URL_PROGRESS_CALLBACKS = URL_PROGRESS_CALLBACKS.filter(
      ([, cb]) => cb != callback
    );
  }
};

export const deleteAdditionalFile = async (
  reportId: string,
  files: string[]
): Promise<string[]> => {
  return (
    await client.asgardDeleteAdditionalFilesAsgardDeleteAdditionalFilesReportIdPost(
      { reportId, requestBody: { files } }
    )
  ).files;
};

export const testDeductibleOrLimitFunction = async (
  reportId: string,
  filename: string | null,
  perilType: client.peril_type,
  functionType: client.function_type
) => {
  return await client.asgardTestPythonFunctionAsgardTestPythonFunctionReportIdPost(
    {
      reportId,
      requestBody: {
        peril_type: perilType,
        extracted_filename: filename,
        function_type: functionType,
      },
    }
  );
};

export const downloadAdditionalFile = async (
  reportId: string,
  file: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_additional_file",
    body: { report_id: reportId, file },
  });
  return data;
};

export const generateBulkEdit = async (
  reportId: string,
  field: string,
  instructions: string
): Promise<[client.Address[], client.Address[]]> => {
  const response = await client.generateBulkEditAsgardGenerateBulkEditPost({
    requestBody: { report_id: reportId, field, instructions },
  });
  return [response.before, response.after];
};

export const downloadReportFile = async (
  reportId: string,
  filename: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_report_file",
    body: { report_id: reportId, filename: filename },
  });
  return data;
};

export const loadAddressesForMap = async (lobs: LOB[]) => {
  return await client.asgardLoadAddressesForMapAsgardLoadAddressesForMapPost({
    requestBody: { lobs },
  });
};

export const addComment = async (reportId: string, message: string) => {
  return await client.asgardAddCommentAsgardAddCommentPost({
    requestBody: { report_id: reportId, message },
  });
};

export const generateUnderwritingPdf = async (
  report_id: string,
  html_content: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/generate_underwriting_pdf",
    body: { report_id, html_content },
    contentType: "application/pdf",
  });
  return data;
};

export const generateQuoteIndicationDraft = async (
  reportId: string,
  perilsInsured: string,
  limitOfLiability: string,
  deductibles: client.DeductibleEntry[],
  premiumRange: string
) => {
  return client.asgardGenerateQuoteIndicationDraftAsgardGenerateQuoteIndicationDraftPost(
    {
      requestBody: {
        report_id: reportId,
        perils_insured: perilsInsured,
        limit_of_liability: limitOfLiability,
        deductibles: deductibles,
        premium_range: premiumRange,
      },
    }
  );
};

export const download360Report = async (valuationId: string) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_360_report",
    body: { valuation_id: valuationId },
  });
  return data;
};

export const downloadUawSheet = async (reportId: string) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_uaw",
    body: { report_id: reportId },
  });
  return data;
};

export const deleteReport = async (reportId: string) => {
  return await client.asgardDeleteReportAsgardDeleteReportPost({
    requestBody: { report_id: reportId },
  });
};

export const enqueueRater = async (
  reportId: string,
  templateId: string | undefined
) => {
  return client.asgardRateSubmissionAsgardRateSubmissionPost({
    requestBody: { report_id: reportId, template_override_id: templateId },
  });
};

export const listReportJobs = async (
  reportIds: string[],
  statuses: client.WorkStatus[] = ["pending", "in_progress", "done", "failed"]
) => {
  const response = await client.asgardListReportJobsAsgardListReportJobsPost({
    requestBody: {
      report_ids: reportIds,
      statuses,
    },
  });
  return response.jobs;
};

export const downloadRaterWorkbook = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/rater/download/${reportId}/${batchId}`,
    contentType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const generateDocument = async (
  reportId: string,
  rater: File,
  templateId: string | undefined,
  useCase: TemplateUseCase,
  format: "pdf" | "docx"
): Promise<{ data: Blob; filename?: string }> => {
  const params = new URLSearchParams();
  params.set("report_id", reportId);
  params.set("use_case", useCase);
  params.set("format", format);
  if (templateId) {
    params.set("template_override_id", templateId);
  }
  const formData = new FormData();
  formData.set("rater", rater);
  return fetchBinary({
    method: "post",
    path: `/asgard/generate_document?${params.toString()}`,
    body: formData,
  });
};

export const previewEml = async (
  reportId: string
): Promise<{ subject: string; eml: string }> => {
  return await client.asgardEmlPreviewAsgardEmlPreviewPost({
    requestBody: { report_id: reportId },
  });
};

export const downloadReportHistory = async (
  reportIds: string[],
  downloadType: client.download_type
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_report_history",
    body: { report_ids: reportIds, download_type: downloadType },
  });
  return data;
};

export const getUpdatingReports = async (since: Date) => {
  return await client.asgardListUpdatingReportsAsgardListUpdatingReportsPost({
    requestBody: { since: since.toISOString() },
  });
};

export const getSovOverview = async (reportId: string) => {
  return await client.asgardGetSovOverviewAsgardGetSovOverviewPost({
    requestBody: { report_id: reportId },
  });
};

export const getAllSovPivots = async (reportId: string) => {
  return await client.asgardGetAllSovPivotsAsgardGetAllSovPivotsPost({
    requestBody: { report_id: reportId },
  });
};

export const getBrokerChartData = async (
  request: client.BrokerAnalyticsCrmBrokerAnalyticsGetData
) => {
  return await client.brokerAnalyticsCrmBrokerAnalyticsGet(request);
};

export const exportBrokerData = async () => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/crm/broker_export",
    body: {},
  });
  return data;
};

export const listTemplates = async () => {
  return await client.asgardListTemplatesAsgardListTemplatesPost();
};

export const createTemplate = async (
  template: File,
  useCase: TemplateUseCase
) => {
  return await client.asgardCreateTemplateAsgardCreateTemplatePost({
    formData: { template },
    useCase,
  });
};

export const deleteTemplate = async (requestBody: client.DeleteTemplate) => {
  return await client.asgardDeleteTemplateAsgardDeleteTemplatePost({
    requestBody,
  });
};
export const updateTemplate = async (requestBody: client.UpdateTemplate) => {
  return await client.asgardUpdateTemplateAsgardUpdateTemplatePost({
    requestBody,
  });
};

export const downloadTemplate = async (template: client.Template) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_template",
    body: { template_id: template.template_id },
  });
  return data;
};

export const generatePolicyNumbers = async (reportId: string) => {
  return await client.asgardGeneratePolicyNumbersAsgardGeneratePolicyNumbersPost(
    { requestBody: { report_id: reportId } }
  );
};

export const generatePolicyFlyApplication = async (
  reportId: string,
  perilsByLayer: Record<string, string>
) => {
  return await client.asgardGeneratePolicyFlyApplicationAsgardGeneratePolicyFlyApplicationPost(
    {
      requestBody: {
        report_id: reportId,
        perils_by_layer: perilsByLayer,
      },
    }
  );
};

export const initiateIcmRequest = async (
  reportId: string
): Promise<client.InitiateRequestEndpointIcmInitiatePostResponse> => {
  return client.initiateRequestEndpointIcmInitiatePost({
    requestBody: { report_id: reportId },
  });
};

export const testIcmRequest = async (
  reportId: string,
  modelMode: client.InitiateTestRequest["model_mode"],
  airModelId: client.InitiateTestRequest["air_model_id"],
  layerIds: string[],
  perilCodes: client.InitiateTestRequest["peril_codes"],
  elevationOverride?: string | null,
  addressLimit?: number | null
): Promise<client.InitiateTestRequestEndpointIcmInitiateTestPostResponse> => {
  return client.initiateTestRequestEndpointIcmInitiateTestPost({
    requestBody: {
      report_id: reportId,
      model_mode: modelMode,
      air_model_id: airModelId,
      peril_codes: perilCodes,
      layer_ids: layerIds,
      elevation_override: elevationOverride,
      address_limit: addressLimit,
    },
  });
};

export const icmRequestStatus = async (
  requestId: string
): Promise<client.RequestStatusIcmStatusRequestIdGetResponse> => {
  return client.requestStatusIcmStatusRequestIdGet({ requestId });
};

// Unified CAT results endpoint
export const catResults = async (
  reportId: string,
  batchId?: string
): Promise<client.CatResultsResponse> => {
  return client.getCatResultsCatResultsReportIdGet({
    reportId,
    batchId: batchId ?? null,
  });
};

export const icmJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "icm",
  });
};

export const downloadIcmArtifact = async (
  reportId: string,
  batchId: string,
  artifact: "analyses" | "request" | "error_response"
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download/${reportId}/${batchId}?type=icm&artifact=${artifact}`,
    contentType: "application/xml",
  });
};

export const downloadAllIcmArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=icm`,
    contentType: "application/zip",
  });
};

export const sendClearanceEmail = async (
  reportId: string,
  status: client.status
) => {
  return client.asgardSendClearanceEmailAsgardSendClearanceEmailPost({
    requestBody: {
      report_id: reportId,
      status: status,
    },
  });
};

// RMS API functions — use generated client where possible
export const initiateRmsRequest = async (
  reportId: string,
  maxLocationOverride: boolean
): Promise<client.RmsInitiateResponse> => {
  return client.initiateRmsRequestRmsInitiatePost({
    requestBody: {
      report_id: reportId,
      layer_ids: null,
      max_location_override: maxLocationOverride,
    },
  });
};

export const rmsRequestStatus = async (
  requestId: string
): Promise<client.RmsStatusResponse> => {
  return client.rmsRequestStatusRmsStatusRequestIdGet({ requestId });
};

export const rmsJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "rms",
  });
};

export const downloadRmsArtifact = async (
  reportId: string,
  batchId: string,
  artifact: "analyses" | "request" | "error_response"
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download/${reportId}/${batchId}?type=rms&artifact=${artifact}`,
    contentType: "application/json",
  });
};

export const downloadAllRmsArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=rms`,
    contentType: "application/zip",
  });
};

export const calculatePremium = async (reportId: string, batchId?: string) => {
  return client.calculatePremiumAsgardPremiumCalculatedPost({
    requestBody: { report_id: reportId, batch_id: batchId },
  });
};

export const getGoogleGroupStats = async (email: string) => {
  return client.asgardGoogleGroupStatsAsgardGoogleGroupStatsPost({
    requestBody: { google_group_email: email },
  });
};

export const getClearanceEmailRecipients = async (
  reportId: string
): Promise<{ recipients: string[] }> => {
  return client.asgardClearanceEmailRecipientsAsgardClearanceEmailRecipientsPost(
    { requestBody: { report_id: reportId } }
  );
};

export const listReportActionLogs = async (): Promise<
  client.ReportActionSummaryWithName[]
> => {
  return client
    .asgardListReportActionLogsAsgardListReportActionLogsPost({
      requestBody: {},
    })
    .then((response) => response.summaries);
};

export const getReportActionLog = async (
  id: string
): Promise<client.GetReportActionLogResponse> => {
  const isEmail = id.startsWith("email:");
  const requestBody = isEmail
    ? { email_message_id: id.slice("email:".length) }
    : { report_id: id };
  return client.asgardGetReportActionLogAsgardGetReportActionLogPost({
    requestBody,
  });
};
